import { CreativeMarkingData } from '@synapex/shared2/gateway-rest-api'
import { ICreative } from '@/shared/api'
import type { FontFamily } from '@/features/analytics/invite-links/ui/Marking/components/MarkingEditor/types'

import { exportMarkingOverlay, type MarkingEditorScale } from './canvas'

export type MarkDataInput = {
    erid: string
    inn: string
    advertiser: string
    mark_media: boolean
    mark_media_color: string
    mark_text: boolean
    mark_media_text: string | undefined

    // Новые поля (опционально для обратной совместимости)
    mark_media_font_size?: number // 4-18
    mark_media_alignment?: 'left' | 'center' | 'right'
    mark_media_font_family?: FontFamily
    mark_media_has_background?: boolean
    mark_media_background_color?: string | null // hex с alpha
    mark_media_position_x?: number // 0-100
    mark_media_position_y?: number // 0-100
    mark_media_scale?: MarkingEditorScale | null // Информация о масштабе для пересчета размеров
}

const defaultLines = ({
    advertiser,
    inn,
    erid,
}: Pick<MarkDataInput, 'inn' | 'erid' | 'advertiser'>) => {
    return [`Реклама. ${advertiser}.`, `ИНН ${inn}. erid: ${erid}`]
}

export const makeMarkingData = async (
    {
        erid,
        inn,
        advertiser,
        mark_media,
        mark_media_color,
        mark_text,
        mark_media_text,
        mark_media_font_size,
        mark_media_alignment,
        mark_media_font_family,
        mark_media_has_background,
        mark_media_background_color,
        mark_media_position_x,
        mark_media_position_y,
        mark_media_scale,
    }: MarkDataInput,
    markableFile?: ICreative['markableFile']
): Promise<CreativeMarkingData> => {
    const result: {
        erid?: string
        text?: string | { advertiser: string; inn: string }
        media?: { overlayPngDataUrl: string; creative_file_id: number }
    } = {}

    if (erid) {
        result.erid = erid
    }

    if (mark_text) {
        // Используем текст из mark_media_text или генерируем дефолтный
        // mark_media_text может быть задан пользователем через поле редактора подписи
        const text =
            mark_media_text ||
            defaultLines({
                erid,
                inn,
                advertiser,
            }).join('\n')
        result.text = text
    }

    if (mark_media) {
        if (markableFile) {
            const canvasImage = await new Promise<HTMLImageElement>(
                (resolve, reject) => {
                    const img = new Image()
                    img.onload = () => resolve(img)
                    img.onerror = reject
                    img.src = markableFile.canvasImageUrl
                }
            )

            // Используем текст из mark_media_text или генерируем дефолтный
            const text =
                mark_media_text ||
                defaultLines({
                    erid,
                    inn,
                    advertiser,
                }).join('\n')

            // Используем параметры редактора, если они есть, иначе значения по умолчанию
            // Передаем scale для правильного пересчета размера шрифта и позиции
            // ВАЖНО: fontSize должен быть в диапазоне 4-18, если не передан - используем 10
            const finalFontSize = mark_media_font_size ?? 10

            const overlayDataUrl = await exportMarkingOverlay({
                imageWidth: canvasImage.naturalWidth,
                imageHeight: canvasImage.naturalHeight,
                text,
                fontSize: Math.max(4, Math.min(18, finalFontSize)), // Ограничиваем диапазон
                textColor: mark_media_color,
                textAlignment: mark_media_alignment ?? 'center',
                fontFamily: mark_media_font_family ?? 'Inter',
                hasBackground: mark_media_has_background ?? false,
                backgroundColor: mark_media_background_color ?? null,
                positionX: mark_media_position_x ?? 50, // центр по умолчанию
                positionY: mark_media_position_y ?? 90, // снизу по умолчанию
                scale: mark_media_scale ?? null, // Информация о масштабе для пересчета
            })

            result.media = {
                creative_file_id: markableFile.id,
                overlayPngDataUrl: overlayDataUrl,
            }
        } else {
            //if mark_media requested, but no markable file provided, fallback to marking text
            const defaultText = defaultLines({
                erid,
                inn,
                advertiser,
            }).join('\n')
            result.text = defaultText
        }
    }

    return result as CreativeMarkingData
}
